import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'
import { furnitureService } from '@/services/FurnitureService'
import { furnitureKeys } from '@/hooks/queryKeys'
import type { FurnitureItemDto, FurnitureItemListDto } from '@lib/dto/furniture-item.dto'
import type { FurnitureUsageDto } from '@lib/dto/plan-impact.dto'
import type { CreateFurnitureItemInput, UpdateFurnitureItemInput } from '@lib/input/furniture-item.input'

/** The tenant's furniture catalog. */
export function useFurniture(
  page?: number,
  venueId?: string | null,
  tags?: readonly string[],
  untagged?: boolean,
): UseQueryResult<FurnitureItemListDto> {
  return useQuery({
    // The venue is part of the key: without it, switching venue would show the
    // previous one's furniture from cache. The tag is in it for the same reason.
    queryKey: [
      ...furnitureKeys.list(page),
      venueId ?? 'all',
      [...(tags ?? [])].sort().join('\u0000'),
      untagged === true,
    ],
    queryFn: () => furnitureService.list(page, venueId, tags, untagged),
    staleTime: 60_000,
    // The previous page's rows stay on screen while the next one loads — see
    // the note on `useVenues`.
    placeholderData: (previous) => previous,
  })
}

/** Create a catalog item, refreshing the list on success. */
export function useCreateFurniture(): UseMutationResult<FurnitureItemDto, Error, CreateFurnitureItemInput> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateFurnitureItemInput) => furnitureService.create(input),
    onSuccess: () => {
      // `all`, not `lists()`: tagging a piece changes the tags in use, and the
      // suggestion list is what keeps free text from sprawling. Invalidating
      // only the rows left it an minute stale, long enough for the next person
      // to type a second spelling of a tag that already existed.
      void queryClient.invalidateQueries({ queryKey: furnitureKeys.all })
    },
  })
}

/** Update a catalog item (rename / reshape), refreshing the list on success. */
export function useUpdateFurniture(): UseMutationResult<
  FurnitureItemDto,
  Error,
  { id: string; input: UpdateFurnitureItemInput }
> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateFurnitureItemInput }) => furnitureService.update(id, input),
    onSuccess: () => {
      // `all`, not `lists()`: tagging a piece changes the tags in use, and the
      // suggestion list is what keeps free text from sprawling. Invalidating
      // only the rows left it an minute stale, long enough for the next person
      // to type a second spelling of a tag that already existed.
      void queryClient.invalidateQueries({ queryKey: furnitureKeys.all })
    },
  })
}

/** Where a catalog piece is in use, for its editor to say before a change reaches it all. */
export function useFurnitureUsage(id: string | undefined): UseQueryResult<FurnitureUsageDto> {
  return useQuery({
    queryKey: furnitureKeys.usage(id ?? ''),
    queryFn: () => furnitureService.usage(id ?? ''),
    enabled: id !== undefined,
  })
}
